import { useState } from "react";
import { CaretUp, X, FilePdf, ArrowsLeftRight, Sparkle } from "@phosphor-icons/react";
import { formatCurrency, formatCurrencyExact } from "../../lib/romEngine";
import { Row } from "./primitives";

/* Phone-only sticky bottom bar for the AI Estimator — shows the live ROM
 * total + low/high band, opens a breakdown sheet on tap, and keeps the
 * "Open in ROM editor" / "Generate proposal" actions one thumb-tap away.
 * Hidden entirely while the chat textarea is focused so it never fights
 * the on-screen keyboard. */
export function MobileTotalBar({ result, inputs, hidden, onOpenEditor, onGenerateProposal }) {
    const [open, setOpen] = useState(false);
    if (!result || hidden) return null;

    const parts = [
        { label: "Labour", v: result.labour_total, color: "var(--rom-navy)" },
        { label: "Consumables", v: result.consumables_total, color: "#94a3b8" },
        { label: "Equipment", v: result.equipment_total, color: "var(--rom-orange)" },
        { label: "Subcontractors", v: result.subs_total || 0, color: "var(--rom-green)" },
        { label: "Misc / Incidentals", v: result.incidentals_total, color: "#a855f7" },
    ].filter((p) => p.v > 0 || ["Labour", "Equipment", "Misc / Incidentals"].includes(p.label));

    return (
        <>
            {open && (
                <div className="sm:hidden fixed inset-0 z-[55] bg-black/50 flex items-end" onClick={() => setOpen(false)} data-testid="ai-mobile-breakdown-sheet">
                    <div className="w-full bg-white rounded-t-[14px] max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="sticky top-0 bg-white flex items-center justify-between px-4 py-3 border-b border-[var(--rom-border)]">
                            <span className="text-sm font-bold">Estimate breakdown</span>
                            <button onClick={() => setOpen(false)} className="p-1.5 -mr-1.5 text-[var(--rom-text-2)]" data-testid="ai-mobile-breakdown-close"><X size={18} weight="bold" /></button>
                        </div>
                        <div className="px-4 pt-3 space-y-2">
                            {parts.map((p) => (
                                <div key={p.label} className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: p.color }} />
                                        <span className="text-[var(--rom-text-2)]">{p.label}</span>
                                    </div>
                                    <span className="font-mono font-semibold">{formatCurrencyExact(p.v)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="mx-4 my-3 border-t border-[var(--rom-border)] pt-3 text-xs space-y-1.5 text-[var(--rom-text-2)]">
                            <div className="flex items-center gap-1.5 font-semibold text-[var(--rom-text)] mb-1">
                                <Sparkle size={12} weight="fill" className="text-[var(--rom-orange)]" /> Crew &amp; timing
                            </div>
                            <Row k="Implied crew" v={`${result.crew_size} people`} />
                            <Row k="Field days" v={`${result.days} d`} />
                            <Row k="Labour hours" v={`${result.field_hours_total} hrs`} />
                        </div>
                        <div className="p-4 pt-1 pb-20 flex flex-col gap-2">
                            <button className="btn btn-primary w-full justify-center" onClick={() => { setOpen(false); onGenerateProposal(); }} data-testid="ai-mobile-generate-proposal">
                                <FilePdf size={14} weight="bold" /> Generate client proposal (PDF)
                            </button>
                            <button className="btn btn-ghost w-full justify-center" onClick={() => { setOpen(false); onOpenEditor(); }} data-testid="ai-mobile-open-editor">
                                <ArrowsLeftRight size={14} weight="bold" /> Open in ROM editor
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="sm:hidden fixed bottom-0 inset-x-0 z-20 bg-white border-t border-[var(--rom-border)] shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" data-testid="ai-mobile-total-bar">
                <div className="flex items-stretch">
                    <button className="flex-1 min-w-0 px-4 py-2.5 text-left active:bg-slate-50" onClick={() => setOpen(true)} data-testid="ai-mobile-total-tap">
                        <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold">
                            ROM total <CaretUp size={9} weight="bold" />
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className="font-display font-black text-xl text-[var(--rom-navy)] truncate" data-testid="ai-mobile-total-value">{formatCurrency(result.grand_total)}</span>
                            <span className="text-[11px] text-[var(--rom-text-2)] truncate">{formatCurrency(result.band_low)}–{formatCurrency(result.band_high)}</span>
                        </div>
                    </button>
                    <button onClick={onOpenEditor} className="px-3 flex flex-col items-center justify-center gap-0.5 border-l border-[var(--rom-border)] text-[var(--rom-text-2)] active:bg-slate-50 shrink-0" data-testid="ai-mobile-bar-editor-btn">
                        <ArrowsLeftRight size={16} weight="bold" />
                        <span className="text-[9px] font-semibold">Editor</span>
                    </button>
                    <button onClick={onGenerateProposal} className="px-3 flex flex-col items-center justify-center gap-0.5 border-l border-[var(--rom-border)] text-[var(--rom-orange-dark)] active:bg-slate-50 shrink-0" data-testid="ai-mobile-bar-proposal-btn">
                        <FilePdf size={16} weight="bold" />
                        <span className="text-[9px] font-semibold">Proposal</span>
                    </button>
                </div>
            </div>
        </>
    );
}
